import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { tenantAdminApi } from '../../utils/tenantAdminApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import DrawerShell from '../../components/DrawerShell';
import { UserPlus, Trash2, KeyRound, Loader2, IndianRupee, Package, Signature } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { v: 'admin',          l: 'Admin' },
  { v: 'doctor',         l: 'Doctor' },
  { v: 'management',     l: 'Management' },
  { v: 'receptiondesk',  l: 'Reception Desk' },
  { v: 'pharmacist',     l: 'Pharmacist' },
  { v: 'pharmacy',       l: 'Pharmacy (full module)' },
  { v: 'purchase',       l: 'Purchase' },
  { v: 'pharmacystores', l: 'Pharmacy Stores' },
  { v: 'storesadmin',    l: 'Stores Admin' },
];

function InviteDialog({ allowedRoles, onDone }) {
  const [open, setOpen] = useState(false);
  const visibleRoles = allowedRoles?.length
    ? ROLES.filter(r => allowedRoles.includes(r.v))
    : ROLES;
  const defaultRole = visibleRoles.find(r => r.v === 'doctor')?.v || visibleRoles[0]?.v || 'doctor';
  const [form, setForm] = useState({ email: '', name: '', role: defaultRole, password: '', specialization: '' });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.email || !form.name || !form.password) {
      toast.error('Email, name and password are required'); return;
    }
    setSaving(true);
    try {
      await tenantAdminApi.invite(form);
      toast.success('User invited');
      setOpen(false); onDone();
      setForm({ email: '', name: '', role: 'doctor', password: '', specialization: '' });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to invite');
    }
    setSaving(false);
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="bg-blue-700 hover:bg-blue-800" data-testid="ta-users-invite-btn">
        <UserPlus className="h-4 w-4 mr-2" /> Invite user
      </Button>
      <DrawerShell
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Team"
        title="Invite a teammate"
        subtitle="They'll sign in with the temporary password and can change it any time"
        accent="#1d4ed8"
        icon={<UserPlus className="h-5 w-5" />}
        width="md"
        testId="ta-invite-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="ta-invite-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Invite
            </Button>
          </>
        }
      >
        <div className="p-5 space-y-3">
          <div className="space-y-1.5">
            <Label>Email *</Label>
            <Input type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="ta-invite-email" />
          </div>
          <div className="space-y-1.5">
            <Label>Name *</Label>
            <Input value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="ta-invite-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="ta-invite-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {visibleRoles.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Specialization</Label>
              <Input value={form.specialization}
                onChange={(e) => setForm({ ...form, specialization: e.target.value })}
                placeholder="(optional)" data-testid="ta-invite-spec" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Temporary password *</Label>
            <Input value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder="Share with the user" data-testid="ta-invite-pass" />
          </div>
        </div>
      </DrawerShell>
    </>
  );
}

function PasswordResetDialog({ u, onDone }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!pw || pw.length < 6) { toast.error('Password must be at least 6 chars'); return; }
    setSaving(true);
    try {
      await tenantAdminApi.updateUser(u.id, { password: pw });
      toast.success('Password reset');
      setOpen(false); setPw(''); onDone();
    } catch (e) { toast.error('Failed'); }
    setSaving(false);
  };
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`ta-user-reset-${u.id}`}>
        <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset password
      </Button>
      <DrawerShell
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Security"
        title="Reset password"
        subtitle={u.email}
        accent="#7c3aed"
        icon={<KeyRound className="h-5 w-5" />}
        width="sm"
        testId="ta-user-reset-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="ta-user-reset-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </>
        }
      >
        <div className="p-5 space-y-1.5">
          <Label>New password for {u.email}</Label>
          <Input value={pw} onChange={(e) => setPw(e.target.value)}
            placeholder="Min 6 chars" data-testid="ta-user-reset-input" />
        </div>
      </DrawerShell>
    </>
  );
}

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allowedRoles, setAllowedRoles] = useState(null);
  const [clinicTypeLabel, setClinicTypeLabel] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try { setUsers(await tenantAdminApi.listUsers()); } catch (_) {}
    setLoading(false);
  };
  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    axios.get(`${API}/api/tenant-admin/capabilities`, { withCredentials: true })
      .then(({ data }) => {
        setAllowedRoles(data?.allowed_roles || null);
        const labels = {
          solo_doctor: 'Single Doctor Clinic',
          reception: 'Clinic with Reception',
          reception_pharmacy: 'Clinic with Reception & Pharmacy',
        };
        if (data?.clinic_type) setClinicTypeLabel(labels[data.clinic_type] || data.clinic_type);
      })
      .catch(() => {});
  }, []);

  const visibleRoles = allowedRoles?.length
    ? ROLES.filter(r => allowedRoles.includes(r.v))
    : ROLES;

  const changeRole = async (u, role) => {
    try {
      await tenantAdminApi.updateUser(u.id, { role });
      toast.success(`Role updated`);
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email}? This cannot be undone.`)) return;
    try {
      await tenantAdminApi.removeUser(u.id);
      toast.success('Removed');
      refresh();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope' }} data-testid="ta-users-title">
            Team
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Invite doctors, pharmacy and purchase staff. Roles control what each user can see in the EMR.
            {clinicTypeLabel && (
              <span className="block text-[11px] text-zinc-600 mt-1">
                Clinic type: <b data-testid="ta-users-clinic-type">{clinicTypeLabel}</b>
              </span>
            )}
          </p>
        </div>
        <InviteDialog allowedRoles={allowedRoles} onDone={refresh} />
      </div>

      <div className="mt-6 bg-white border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b text-left text-zinc-600">
            <tr>
              <th className="px-4 py-3 font-medium">Name</th>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Role</th>
              <th className="px-4 py-3 font-medium">Joined</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="5" className="px-4 py-8 text-center text-zinc-500">Loading…</td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan="5" className="px-4 py-8 text-center text-zinc-500">No users yet.</td></tr>
            ) : users.map((u) => (
              <tr key={u.id} className="border-b last:border-0" data-testid={`ta-user-row-${u.email}`}>
                <td className="px-4 py-3 font-medium text-zinc-900">{u.name || '—'}</td>
                <td className="px-4 py-3 text-zinc-700">{u.email}</td>
                <td className="px-4 py-3">
                  <Select value={u.role || 'doctor'} onValueChange={(v) => changeRole(u, v)}>
                    <SelectTrigger className="h-8 w-36 text-xs" data-testid={`ta-user-role-${u.email}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {visibleRoles.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3 text-zinc-500 text-xs">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—'}
                </td>
                <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                  {(u.role === 'doctor' || u.role === 'admin') && (
                    <SignatureDialog u={u} onDone={refresh} />
                  )}
                  {(u.role === 'doctor') && (
                    <FeesDialog u={u} onDone={refresh} />
                  )}
                  <InventoryAccessDialog u={u} onDone={refresh} />
                  <PasswordResetDialog u={u} onDone={refresh} />
                  <Button variant="outline" size="sm" onClick={() => remove(u)}
                    className="text-red-700 border-red-200 hover:bg-red-50"
                    data-testid={`ta-user-remove-${u.email}`}>
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SignatureDialog({ u, onDone }) {
  // Doctor-specific: edits the "Department" (a.k.a. specialization) and
  // the long ceremonial line printed under the signature on every
  // printed prescription. Both fields save via PATCH /api/tenant-admin/
  // users/{id} and take effect immediately for the next print.
  const [open, setOpen] = useState(false);
  const [spec, setSpec] = useState(u.specialization || '');
  const [sig, setSig] = useState(u.signature_title || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await tenantAdminApi.updateUser(u.id, {
        specialization: spec.trim(),
        signature_title: sig.trim(),
      });
      toast.success('Signature updated');
      setOpen(false);
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to update signature');
    }
    setSaving(false);
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`ta-user-signature-${u.email}`}>
        <Signature className="h-3.5 w-3.5 mr-1" /> Signature
      </Button>
      <DrawerShell
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Print Signature"
        title={`Signature for ${u.name || u.email}`}
        subtitle="Shows on every printed prescription. Takes effect on the next print."
        accent="#4338ca"
        icon={<Signature className="h-5 w-5" />}
        width="md"
        testId="ta-user-signature-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-indigo-700 hover:bg-indigo-800" data-testid="ta-sig-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </>
        }
      >
        <div className="p-5 space-y-4">
          <div className="space-y-1.5">
            <Label>Department / Specialization</Label>
            <Input value={spec}
              onChange={(e) => setSpec(e.target.value)}
              placeholder="e.g. Consultant GI & HPB Surgeon"
              data-testid="ta-sig-spec" />
            <p className="text-[11px] text-zinc-500">
              Shown in the dashboard header and directly under the doctor's name on print.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Below-signature line (optional)</Label>
            <Input value={sig}
              onChange={(e) => setSig(e.target.value)}
              placeholder="e.g. Consultant Gastro Intestinal & Hepato Pancreatico Biliary specialist"
              data-testid="ta-sig-title" />
            <p className="text-[11px] text-zinc-500">
              Optional italic line rendered <b>below</b> the department, useful for full titles or affiliations. Leave blank to hide.
            </p>
          </div>
          {/* Live preview */}
          <div className="mt-2 p-4 rounded-lg border border-dashed border-zinc-300 bg-zinc-50">
            <div className="text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-2">Preview</div>
            <div className="text-right leading-tight">
              <div className="text-sm font-bold text-zinc-900">Dr. {u.name || 'Doctor Name'}</div>
              <div className="text-[11px] font-semibold text-indigo-900 mt-0.5">{spec || 'Consultant'}</div>
              {sig && (
                <div className="text-[10px] italic text-indigo-900 mt-0.5">{sig}</div>
              )}
            </div>
          </div>
        </div>
      </DrawerShell>
    </>
  );
}

function FeesDialog({ u, onDone }) {
  const [open, setOpen] = useState(false);
  const fees = u.fees || {};
  const [firstVisit, setFirstVisit] = useState(fees.first_visit ?? '');
  const [followup, setFollowup] = useState(fees.followup ?? '');
  const [walkin, setWalkin] = useState(fees.walkin ?? '');
  const [window, setWindow] = useState(u.followup_window_days ?? 30);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        fees: {
          first_visit: firstVisit === '' ? null : Number(firstVisit),
          followup:    followup === ''   ? null : Number(followup),
          walkin:      walkin === ''     ? null : Number(walkin),
        },
        followup_window_days: Number(window) || 30,
      };
      await tenantAdminApi.updateUser(u.id, payload);
      toast.success('Fees updated');
      setOpen(false);
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to update fees');
    }
    setSaving(false);
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`ta-user-fees-${u.email}`}>
        <IndianRupee className="h-3.5 w-3.5 mr-1" /> Fees
      </Button>
      <DrawerShell
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Billing"
        title={`Fees for ${u.name || u.email}`}
        subtitle="Reception auto-applies the matching tier at check-in"
        accent="#059669"
        icon={<IndianRupee className="h-5 w-5" />}
        width="md"
        testId="ta-user-fees-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="ta-fee-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </>
        }
      >
        <div className="p-5 space-y-3">
          <p className="text-xs text-zinc-500">
            Leave any field blank to fall back to whichever tier is set.
          </p>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">First visit ₹</Label>
              <Input type="number" min="0" value={firstVisit}
                onChange={(e) => setFirstVisit(e.target.value)}
                data-testid="ta-fee-first" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Follow-up ₹</Label>
              <Input type="number" min="0" value={followup}
                onChange={(e) => setFollowup(e.target.value)}
                data-testid="ta-fee-followup" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Walk-in / repeat ₹</Label>
              <Input type="number" min="0" value={walkin}
                onChange={(e) => setWalkin(e.target.value)}
                data-testid="ta-fee-walkin" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Follow-up window (days)</Label>
            <Input type="number" min="0" max="3650" value={window}
              onChange={(e) => setWindow(e.target.value)}
              data-testid="ta-fee-window" />
            <p className="text-[10px] text-zinc-500">
              Revisits within this window are billed as follow-up; later visits use the walk-in rate.
            </p>
          </div>
        </div>
      </DrawerShell>
    </>
  );
}

function InventoryAccessDialog({ u, onDone }) {
  const [open, setOpen] = useState(false);
  const implicit = u.role === 'admin' || u.role === 'pharmacist';
  const initial = implicit ? true : !!(u.capabilities?.can_manage_inventory);
  const [granted, setGranted] = useState(initial);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await tenantAdminApi.updateUser(u.id, {
        capabilities: { ...(u.capabilities || {}), can_manage_inventory: !!granted },
      });
      toast.success(granted ? 'Can now manage inventory' : 'Inventory write access removed');
      setOpen(false);
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    }
    setSaving(false);
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid={`ta-user-inventory-${u.email}`}>
        <Package className="h-3.5 w-3.5 mr-1" /> Inventory
      </Button>
      <DrawerShell
        open={open}
        onClose={() => setOpen(false)}
        eyebrow="Permissions"
        title={`Inventory access for ${u.name || u.email}`}
        subtitle="View access is automatic — write access requires explicit grant"
        accent="#0891b2"
        icon={<Package className="h-5 w-5" />}
        width="md"
        testId="ta-user-inventory-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || implicit} className="bg-blue-700 hover:bg-blue-800" data-testid="ta-inv-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Save
            </Button>
          </>
        }
      >
        <div className="p-5 space-y-3">
          <label className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50">
            <input
              type="checkbox"
              checked={granted}
              disabled={implicit}
              onChange={(e) => setGranted(e.target.checked)}
              data-testid="ta-inv-toggle"
            />
            <div>
              <div className="text-sm font-medium text-zinc-900">
                Can add / edit medicine stock
              </div>
              {implicit && (
                <div className="text-[11px] text-zinc-500">
                  Implicit for role <b>{u.role}</b> — cannot be revoked.
                </div>
              )}
            </div>
          </label>
        </div>
      </DrawerShell>
    </>
  );
}
